import { useEffect } from 'react';
import { axios_api_instance } from '../utils/request';
import { AxiosRequestConfig } from 'axios';
import { getNewAuthToken } from '../services/authentication/AuthTokenRefresh';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import {
  clearUserSession,
  updateUserAuthToken,
} from '../store/slices/UserSession/userSession';
import apiErrorHandler from '../utils/apiErrorHandler';

type failedRequestQueueItem = {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
};

type FreshTokenType = {
  signedAccessToken: string;
};

let failedRequestsQueue: Array<failedRequestQueueItem> = [];
let isRefreshing = false;

/**
 * @description This hook is meant to handle all axios interceptors for both request and response, It should be used inside the app component for expected result.
 * @example import useAxiosInterceptor from " useAxiosInterceptor file path"
 * const App : React.FC = () => {
 *      useAxiosInterceptor();
 * }
 */
export default function useAxiosInterceptor() {
  const history = useHistory();
  const dispatch = useDispatch();

  // RESEND EVERY FAILED REQUEST IN QUEUE
  const resendFailedRequests = (token: string) => {
    if (failedRequestsQueue.length < 1) return;

    failedRequestsQueue.forEach(({ config, reject, resolve }) => {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
      axios_api_instance(config)
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });

    failedRequestsQueue = [];
  };

  useEffect(() => {
    const responseInterceptor = axios_api_instance.interceptors.response.use(
      (res) => res,
      async (resError) => {
        const customError = apiErrorHandler(resError);

        // IF THE ERROR IS NOT PROTECTED, JUST REJECT THE ERROR
        const isProtected = Boolean(resError.config.headers?.Authorization);

        if (
          resError.response &&
          resError.response.status === 401 &&
          isProtected
        ) {
          const originalReq: AxiosRequestConfig = resError.config;

          // ADD REQUEST TO QUEUE IF A NEW TOKEN IS ALREADY BEING REQUESTED
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedRequestsQueue?.push({
                reject,
                resolve,
                config: originalReq,
              });
            });
          }

          // IF A NEW TOKEN HAS NOT BEEN REQUESTED, REQUEST IT AND RESEND FAILED REQUESTS INCLUDING THIS ONE
          isRefreshing = true;
          try {
            const newToken: FreshTokenType = await getNewAuthToken();

            //UPDATE USER SESSION
            dispatch(updateUserAuthToken(newToken));

            //RESEND FAILED REQUESTS
            resendFailedRequests(newToken.signedAccessToken);

            //UPDATE ORIGINAL REQUEST
            // originalReq["_retry"] = true
            originalReq.headers = originalReq.headers || {};
            originalReq.headers.Authorization = `Bearer ${newToken.signedAccessToken}`;
            return axios_api_instance(originalReq);
          } catch {
            // REJECT ALL FAILED REQUESTS IN QUEUE
            dispatch(clearUserSession());
            history?.replace('/auth/sign-in');
          } finally {
            isRefreshing = false;
          }
        }
        return Promise.reject(customError);
      }
    );

    return () => {
      axios_api_instance.interceptors.response.eject(responseInterceptor);
    };
  }, []);
}
